import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../api/client';

const MOOD_EMOJI = { great: '😄', good: '🙂', okay: '😐', rough: '😔' };

export default function CalendarView({ onSelectDay }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dayPlans, setDayPlans] = useState([]);

  const load = useCallback(async () => {
    const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    const data = await api.getCalendar(from, to);
    setDayPlans(data);
  }, [currentMonth]);

  useEffect(() => { load(); }, [load]);

  const planByDate = {};
  dayPlans.forEach(p => { planByDate[p.date?.split('T')[0]] = p; });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDow = (getDay(monthStart) + 6) % 7; // Monday-first
  const DOW = ['Mo','Di','Mi','Do','Fr','Sa','So'];

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>Tagebuch</h1>
        <button className="btn btn-ghost btn-sm" onClick={() => setCurrentMonth(m => subMonths(m, 1))}><ChevronLeft size={14} /></button>
        <span style={{ fontWeight: 500, fontSize: 14, minWidth: 140, textAlign: 'center' }}>
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={() => setCurrentMonth(m => addMonths(m, 1))}><ChevronRight size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => setCurrentMonth(new Date())}>Heute</button>
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-3)', fontWeight: 500, padding: '4px 0' }}>{d}</div>
        ))}
        {Array.from({ length: startDow }).map((_, i) => <div key={`empty-${i}`} />)}
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const plan = planByDate[dateStr];
          const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
          const hasPlan = !!plan;

          return (
            <div key={dateStr} onClick={() => onSelectDay && onSelectDay(dateStr)}
              style={{
                minHeight: 72, border: `1px solid ${isToday ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)', padding: '6px 8px', cursor: 'pointer',
                background: hasPlan ? 'var(--bg-1)' : 'transparent',
                transition: 'all 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
              onMouseLeave={e => e.currentTarget.style.background = hasPlan ? 'var(--bg-1)' : 'transparent'}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--accent)' : 'var(--text)' }}>
                  {format(day, 'd')}
                </span>
                {plan?.mood && <span style={{ fontSize: 14 }}>{MOOD_EMOJI[plan.mood]}</span>}
              </div>
              {hasPlan && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', marginBottom: 4 }} />
                  {plan.retro && (
                    <div style={{ fontSize: 10, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                      {plan.retro}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent entries */}
      <div>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 18, marginBottom: 12 }}>Letzte Einträge</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dayPlans.filter(p => p.retro).slice(0, 5).map(plan => (
            <div key={plan.id} className="card" style={{ cursor: 'pointer' }}
              onClick={() => onSelectDay && onSelectDay(plan.date?.split('T')[0])}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  {format(new Date(plan.date), 'EEEE, dd. MMMM', { locale: de })}
                </span>
                {plan.mood && <span>{MOOD_EMOJI[plan.mood]}</span>}
              </div>
              <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.5, display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {plan.retro}
              </p>
            </div>
          ))}
          {dayPlans.filter(p => p.retro).length === 0 && (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Noch keine Tagebucheinträge in diesem Monat</div>
          )}
        </div>
      </div>
    </div>
  );
}
